import { useEffect, useState } from "react";
import { MessageSquare, FileText } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError, GRADES, gradeLabel } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const input = "px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]";

const Transcript = () => {
  const [grade, setGrade] = useState("5");
  const [msgs, setMsgs] = useState(null);
  const load = async () => setMsgs((await http.get("/admin/school-chat/transcript", { params: { grade } })).data);
  return (
    <div className="mt-6">
      <div className="flex gap-2 items-center"><select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="school-tx-grade" className={input}>{GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}</select>
        <button onClick={load} data-testid="school-tx-load" className="px-4 py-2 rounded-full border border-[#E6DEC3] text-sm inline-flex items-center gap-1"><FileText className="w-4 h-4" /> View transcript</button></div>
      {msgs && <div className="mt-3 max-h-72 overflow-y-auto space-y-1" data-testid="school-tx-list">
        {msgs.map((m) => <p key={m.id} className={`text-sm ${m.blocked ? "text-red-700" : ""}`}><span className="text-stone-400 text-xs">{new Date(m.created_at).toLocaleString()}</span> <b>{m.name}</b>: {m.text}{m.blocked && ` [BLOCKED: ${m.reason}]`}</p>)}
        {!msgs.length && <p className="text-sm text-stone-500">No messages for this grade.</p>}</div>}
    </div>
  );
};

export const SchoolChatSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/admin/school-chat/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { const r = await http.put("/admin/school-chat/settings", { enabled: s.enabled, start: s.start, end: s.end, days: s.days }); setS(r.data); toast.success("School-Day Chat hours saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleDay = (i) => setS({ ...s, days: s.days.includes(i) ? s.days.filter((d) => d !== i) : [...s.days, i] });
  return (
    <section className="mt-12 rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid="school-chat-settings">
      <div className="flex flex-wrap items-center gap-4">
        <h2 className="font-display text-2xl text-[#2C4A3E] inline-flex items-center gap-2 mr-auto"><MessageSquare className="w-5 h-5" /> School-Day Chat</h2>
        <label className="inline-flex items-center gap-2 text-sm">Enabled <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="school-chat-enabled" /></label>
      </div>
      <p className="text-sm text-stone-500 mt-1" data-testid="school-chat-hours">Current: {s.hours} (each student's local school time)</p>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <input type="time" value={s.start} onChange={(e) => setS({ ...s, start: e.target.value })} data-testid="school-chat-start" className={input} />
        <span className="text-sm">to</span>
        <input type="time" value={s.end} onChange={(e) => setS({ ...s, end: e.target.value })} data-testid="school-chat-end" className={input} />
        <div className="flex gap-1">{DAYS.map((d, i) => <button key={d} onClick={() => toggleDay(i)} data-testid={`school-chat-day-${i}`} className={`w-11 py-2 rounded-full text-xs border ${s.days.includes(i) ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3]"}`}>{d}</button>)}</div>
        <button onClick={save} data-testid="school-chat-save" className="px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm">Save hours</button>
      </div>
      <Transcript />
    </section>
  );
};

export const MidyearToggle = () => {
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/admin/midyear").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const set = async (term, open) => { try { setS((await http.put("/admin/midyear", { open, term })).data); toast.success(`${term === "winter" ? "Mid-year" : "End-of-year"} check-in ${open ? "opened" : "closed"}`); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section className="mt-8 rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid="midyear-settings">
      <h2 className="font-display text-2xl text-[#2C4A3E]">Check-in windows</h2>
      {[["winter", "Mid-year check-in", "January 1–31", "midyear-switch"], ["spring", "End-of-year check-in", "May 1–31", "spring-switch"]].map(([t, l, w, id]) => (
        <div key={t} className="mt-4 flex flex-wrap items-center gap-4">
          <p className="flex-1 min-w-[220px] text-sm text-stone-600"><b className="text-[#2C4A3E]">{l}</b> opens automatically {w}{s[t]?.in_window ? " (open now)" : ""}. Turn on to open it early.</p>
          <label className="inline-flex items-center gap-2 text-sm">Open now <Switch checked={!!s[t]?.open} onCheckedChange={(v) => set(t, v)} data-testid={id} /></label>
        </div>
      ))}
    </section>
  );
};
